import { useTranslation } from "react-i18next";
import {
  Text,
  VStack,
  RadioGroup,
  Radio,
  Box,
  Stack,
  Collapse,
} from "@chakra-ui/react";
import { FormProvider, Controller } from "react-hook-form";
import {
  SectionContainer,
  SectionHeader,
  Card,
  CardTitle,
  CardOptionsContainer,
  OptionRow,
  EnrollmentMonthBox,
} from "../../../pages/kpec-admin/course-management/style";
import { CalenderIcon } from "@root/assets/icons";
import { BluePlusIcon } from "@root/assets/icons";
import { useEffect, useState, useRef } from "react";
import Schedule from "@root/components/schedule/Schedule";
import { RenderSelectedFields } from "@root/components/kpec-admin/course-management/RenderSelectedChildren";
import { useCourseManagementContext } from "@root/context/course-management-context";
import { formatToShortDate } from "@root/utils/date-helper";
import {
  chaptersWithVideos,
  courseAccessOptions,
  enrollmentOptions,
  EnrollmentOptionType,
  Present,
} from "@root/constants";
import { CourseAccessOptionsList } from "../../../pages/kpec-admin/course-management/CourseAccessOptionsList";
import { useParams } from "react-router-dom";
import { CourseSuccessModal } from "./courseSuccessCard";

const CourseStep3 = ({ defaultValues }: { defaultValues?: any }) => {
  const { t } = useTranslation("common");
  const {
    methods3: methods,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
  } = useCourseManagementContext();
  const { register, handleSubmit, control, watch, setValue } = methods;

  const selectedEnrollmentOption = watch("enrollmentOption");
  const isFreeAccessEnabled = watch("freeAccessEnabled");
  const makeWholeCourseFree = watch("makeWholeCourseFree");
  const selectedChaptersObj = watch("selectedChapters");
  const selectedVideosObj = watch("selectedVideos");
  const [enrollmentMonths, setEnrollmentMonths] = useState([1, 2, 3, 4]);

  const [showDurationDropdown, setShowDurationDropdown] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [showChapterDropdown, setShowChapterDropdown] = useState(false);
  const [expandedChapters, setExpandedChapters] = useState<
    Record<string, boolean>
  >({});
  const isMounted = useRef(false);

  const {
    courseChaptersData,
    getCourseChapters,
    getCourseChaptersLoading,
    updateCourseId,
    currentCourse,
    courseData,
  } = useCourseManagementContext();
  const { id } = useParams();

  // Updated data structure with isSelected property
  const chaptersData = courseChaptersData?.rows?.map((chapter: any) => ({
    id: chapter?.id,
    name: chapter?.title,
    videos: chapter?.courseVideos?.map((video: any) => ({
      id: video?.id,
      name: video?.title,
      isSelected: video?.isFree || false, // Use isFree from API response
    })),
  }));

  // Helper function to initialize selected states from chaptersData
  const initializeSelectionStates = (chapters: any[]) => {
    const initialChapters: Record<string, boolean> = {};
    const initialVideos: Record<string, boolean> = {};

    chapters?.forEach((chapter) => {
      // Check if all videos in chapter are selected
      const allVideosSelected = chapter.videos?.every(
        (video: any) => video.isSelected
      );

      // Set chapter as selected only if ALL videos are selected
      initialChapters[chapter.id] = allVideosSelected;

      // Set individual video selections
      chapter.videos?.forEach((video: any) => {
        initialVideos[video.id] = video.isSelected;
      });
    });

    return { initialChapters, initialVideos };
  };

  useEffect(() => {
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        setValue(key, value);
      });
    } else if (!id) {
      // Reset form for new course
      setValue("enrollmentOption", EnrollmentOptionType.none);
      setValue("enrollmentDurationValue", "");
      setValue("enrollmentStartDate", "");
      setValue("enrollmentEndDate", "");
      setValue("displayOnHome", false);
      setValue("freeAccessEnabled", false);
      setValue("makeWholeCourseFree", false);
      setValue("selectedChapters", {});
      setValue("selectedVideos", {});
    }
  }, [defaultValues, setValue, id]);

  useEffect(() => {
    if (updateCourseId || currentCourse?.id) {
      getCourseChapters(updateCourseId ?? currentCourse?.id);
    }
  }, [updateCourseId, currentCourse?.id]);

  // Initialize selection states when chaptersData is loaded
  useEffect(() => {
    if (chaptersData?.length) {
      const { initialChapters, initialVideos } =
        initializeSelectionStates(chaptersData);

      // Only set if not already set (to avoid overwriting user selections)
      if (
        !selectedChaptersObj ||
        Object.keys(selectedChaptersObj).length === 0
      ) {
        setValue("selectedChapters", initialChapters);
      }
      if (!selectedVideosObj || Object.keys(selectedVideosObj).length === 0) {
        setValue("selectedVideos", initialVideos);
      }
    }
  }, [chaptersData, setValue]);

  // Mark component as mounted so clearing effects only fire on real user changes
  useEffect(() => {
    isMounted.current = true;
  }, []);

  // Track if course data has been initialized to prevent re-initialization
  const [courseDataInitialized, setCourseDataInitialized] = useState(false);

  useEffect(() => {
    // Only initialize once when courseData is first loaded
    if (id && courseData && !courseDataInitialized) {
      // Set visibility options
      setValue("displayOnHome", courseData?.displayOnHome || false);

      // Initialize enrollment options based on backend data
      if (courseData?.enrollmentDuration) {
        // If enrollment duration value exists, select the duration option
        setValue("enrollmentOption", EnrollmentOptionType.duration);
        setValue(
          "enrollmentDurationValue",
          String(courseData?.enrollmentDuration)
        );
        setShowDurationDropdown(true);
        setShowPeriodDropdown(false);
        
        // Ensure the month is available in enrollmentMonths array
        const durationValue = (courseData?.enrollmentDuration).toString();
        if (durationValue && !enrollmentMonths.includes(durationValue)) {
          setEnrollmentMonths((prev) => {
            const newMonths = [...prev];
            // Add months up to the required value if not present
            for (let i = Math.max(...prev) + 1; i <= durationValue; i++) {
              if (i <= 12) {
                // Don't exceed 12 months
                newMonths.push(i);
              }
            }
            return newMonths.sort((a, b) => a - b);
          });
        }
        
        // Clear period dates
        setValue("enrollmentStartDate", "");
        setValue("enrollmentEndDate", "");
        setStartDate(null);
        setEndDate(null);
      } else if (
        courseData?.enrollmentPeriodStart &&
        courseData?.enrollmentPeriodEnd
      ) {
        // If enrollment period dates exist, select the period option
        setValue("enrollmentOption", EnrollmentOptionType.period);
        setShowDurationDropdown(false);
        setShowPeriodDropdown(true);
        
        // Clear duration value
        setValue("enrollmentDurationValue", "");

        // Set dates from course data - only if context dates are not already set
        if (courseData.enrollmentPeriodStart && !startDate) {
          const startDateObj = new Date(courseData.enrollmentPeriodStart);
          if (!isNaN(startDateObj.getTime())) {
            setStartDate(startDateObj);
            setValue("enrollmentStartDate", courseData.enrollmentPeriodStart);
          }
        }
        if (courseData.enrollmentPeriodEnd && !endDate) {
          const endDateObj = new Date(courseData.enrollmentPeriodEnd);
          if (!isNaN(endDateObj.getTime())) {
            setEndDate(endDateObj);
            setValue("enrollmentEndDate", courseData.enrollmentPeriodEnd);
          }
        }
      }
      
      // If neither duration nor period exists, default to none
      if (!courseData?.enrollmentDuration && !courseData?.enrollmentPeriodStart) {
        setValue("enrollmentOption", EnrollmentOptionType.none);
        setValue("enrollmentDurationValue", "");
        setShowDurationDropdown(false);
        setShowPeriodDropdown(false);
      }

      setCourseDataInitialized(true);
    } else if (!id && !courseDataInitialized) {
      // For new courses, default to none (always visible)
      setValue("enrollmentOption", EnrollmentOptionType.none);
      setValue("enrollmentDurationValue", "");
      setShowDurationDropdown(false);
      setShowPeriodDropdown(false);
      setCourseDataInitialized(true);
    }
  }, [courseData, id, setValue, enrollmentMonths, setStartDate, setEndDate, courseDataInitialized, startDate, endDate]);

  // Initialize free access settings when editing a course
  useEffect(() => {
    if (
      id &&
      courseChaptersData?.rows &&
      courseChaptersData.rows.length > 0 &&
      (!selectedVideosObj || Object.keys(selectedVideosObj).length === 0)
    ) {
      // Check if any video has isFree = true
      const hasFreeVideos = courseChaptersData.rows.some((chapter: any) =>
        chapter?.courseVideos?.some((video: any) => video?.isFree === true)
      );

      if (hasFreeVideos) {
        // Set freeAccessEnabled to true
        setValue("freeAccessEnabled", true);

        // Build selectedVideos and selectedChapters based on isFree videos
        const videosToSelect: Record<string, boolean> = {};
        const chaptersToSelect: Record<string, boolean> = {};

        courseChaptersData.rows.forEach((chapter: any) => {
          const freeVideosInChapter =
            chapter?.courseVideos?.filter(
              (video: any) => video?.isFree === true
            ) || [];

          if (freeVideosInChapter.length > 0) {
            // Mark all free videos as selected
            freeVideosInChapter.forEach((video: any) => {
              videosToSelect[video.id] = true;
            });

            // If all videos in chapter are free, mark chapter as selected
            const allVideosFree = chapter?.courseVideos?.every(
              (video: any) => video?.isFree === true
            );
            if (allVideosFree) {
              chaptersToSelect[chapter.id] = true;
            }
          }
        });

        // Only update if we have selections to make
        if (Object.keys(videosToSelect).length > 0) {
          setValue("selectedVideos", videosToSelect);
        }
        if (Object.keys(chaptersToSelect).length > 0) {
          setValue("selectedChapters", chaptersToSelect);
        }
      }
    }
  }, [id, courseChaptersData, setValue, selectedVideosObj]);


  // Sync form values when startDate or endDate changes (for period option)
  // This allows users to change dates when period option is selected
  useEffect(() => {
    if (
      selectedEnrollmentOption === EnrollmentOptionType.period &&
      startDate &&
      endDate
    ) {
      // Update form values whenever dates change in context (always, not just after initialization)
      if (startDate instanceof Date && !isNaN(startDate.getTime())) {
        setValue("enrollmentStartDate", startDate.toISOString(), { shouldDirty: false });
      }
      if (endDate instanceof Date && !isNaN(endDate.getTime())) {
        setValue("enrollmentEndDate", endDate.toISOString(), { shouldDirty: false });
      }
    }
  }, [startDate, endDate, selectedEnrollmentOption, setValue]);

  useEffect(() => {
    if (!isMounted.current) return;
    if (!isFreeAccessEnabled) {
      // When "Free Access" is disabled, clear selected chapters and videos
      setValue("freeAccessChapters", []);
      setValue("selectedChapters", {});
      setValue("selectedVideos", {});
    }
  }, [isFreeAccessEnabled, setValue]);

  // Handle parent-child relationships
  useEffect(() => {
    if (!isMounted.current) return;
    if (!isFreeAccessEnabled) {
      setValue("makeWholeCourseFree", false);
      setValue("selectedChapters", {});
      setValue("selectedVideos", {});
    }
  }, [isFreeAccessEnabled, setValue]);

  useEffect(() => {
    if (!isMounted.current) return;
    if (!makeWholeCourseFree) {
      setValue("selectedChapters", {});
      setValue("selectedVideos", {});
    }
  }, [makeWholeCourseFree, setValue]);

  // Track previous enrollment option to detect changes
  const prevEnrollmentOptionRef = useRef<string | null>(null);

  useEffect(() => {
    setShowDurationDropdown(
      selectedEnrollmentOption === EnrollmentOptionType.duration
    );
    setShowPeriodDropdown(
      selectedEnrollmentOption === EnrollmentOptionType.period
    );
    setShowChapterDropdown(isFreeAccessEnabled === true);

    // Only clear opposite values when the option actually changes (not on initial render)
    const optionChanged = prevEnrollmentOptionRef.current !== null && 
                          prevEnrollmentOptionRef.current !== selectedEnrollmentOption;

    if (selectedEnrollmentOption === EnrollmentOptionType.none) {
      // Clear all enrollment values when "No Enrollment Period" is selected
      if (optionChanged) {
        setValue("enrollmentDurationValue", "");
        setValue("enrollmentStartDate", "");
        setValue("enrollmentEndDate", "");
        setStartDate(null);
        setEndDate(null);
      }
    } else if (selectedEnrollmentOption === EnrollmentOptionType.duration) {
      // Clear period dates when switching to duration (only if option changed)
      if (optionChanged) {
        setValue("enrollmentStartDate", "");
        setValue("enrollmentEndDate", "");
        setStartDate(null);
        setEndDate(null);
      }

      // Set default duration value if not set (only for new courses or when switching)
      const currentDuration = watch("enrollmentDurationValue");
      if (
        (!currentDuration ||
        currentDuration === "" ||
        currentDuration === null ||
        currentDuration === undefined) &&
        (optionChanged || !id)
      ) {
        setValue("enrollmentDurationValue", "1");
      }
    } else if (selectedEnrollmentOption === EnrollmentOptionType.period) {
      // Clear duration value when switching to period (only if option changed)
      if (optionChanged) {
        setValue("enrollmentDurationValue", "");
      }

      // Initialize dates if they don't exist when period option is selected
      // Don't overwrite existing dates when just loading or if dates already exist
      if ((optionChanged || !id) && (!startDate || !endDate)) {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setStartDate(today);
        setEndDate(tomorrow);
      }
    }

    // Update previous option tracker
    if (selectedEnrollmentOption) {
      prevEnrollmentOptionRef.current = selectedEnrollmentOption;
    }
  }, [
    selectedEnrollmentOption,
    isFreeAccessEnabled,
    setValue,
    watch,
    setStartDate,
    setEndDate,
    startDate,
    endDate,
    id,
  ]);

  // Handle form submission
  const onSubmit = (data: FormValuesStep3) => {
    const payload = {
      displayOnHome: data?.displayOnHome,
      freeAccessEnabled: data?.freeAccessEnabled,
      enrolmentDuration: data?.enrollmentDurationValue,
      enrolmentStartDate: data?.enrollmentStartDate,
      enrolmentEndDate: data?.enrollmentEndDate,
    };
    console.log("onSubmit setPreference", data);
    // publishCourse(payload);
  };

  // Handle chapter selection
  const handleChapterChange = (chapterId: string, checked: boolean) => {
    const currentChapters = { ...selectedChaptersObj };
    const currentVideos = { ...selectedVideosObj };

    currentChapters[chapterId] = checked;

    // If chapter is unchecked, uncheck all its videos
    if (!checked) {
      const chapter = chaptersData?.find((c: any) => c.id === chapterId);
      if (chapter) {
        chapter.videos?.forEach((video: any) => {
          currentVideos[video.id] = false;
        });
      }
    } else {
      // If chapter is checked, ensure all its videos are checked
      const chapter = chaptersData?.find((c: any) => c.id === chapterId);
      if (chapter) {
        chapter.videos?.forEach((video: any) => {
          currentVideos[video.id] = true;
        });
      }
    }

    setValue("selectedChapters", currentChapters);
    setValue("selectedVideos", currentVideos);
  };

  // Handle video selection
  const handleVideoChange = (
    videoId: string,
    chapterId: string,
    checked: boolean
  ) => {
    const currentVideos = { ...selectedVideosObj };
    const currentChapters = { ...selectedChaptersObj };

    currentVideos[videoId] = checked;

    // Check if all videos in chapter are selected
    const chapter = chaptersData?.find((c: any) => c.id === chapterId);
    if (chapter) {
      const allVideosSelected = chapter.videos?.every(
        (video: any) => currentVideos[video.id] === true
      );

      // If all videos are selected, check the chapter
      if (allVideosSelected && checked) {
        currentChapters[chapterId] = true;
      }
      // If any video is unchecked, uncheck the chapter
      else if (!checked) {
        currentChapters[chapterId] = false;
      }
    }

    setValue("selectedVideos", currentVideos);
    setValue("selectedChapters", currentChapters);
  };

  // Toggle chapter expansion
  const toggleChapterExpansion = (chapterId: string) => {
    setExpandedChapters((prev) => ({
      ...prev,
      [chapterId]: !prev[chapterId],
    }));
  };

  // Check if chapter is indeterminate (some but not all videos selected)
  const isChapterIndeterminate = (chapterId: string) => {
    const chapter = chaptersData?.find((c: any) => c.id === chapterId);
    if (!chapter) return false;

    const selectedVideosCount =
      chapter.videos?.filter((video: any) => selectedVideosObj[video.id])
        .length || 0;

    return (
      selectedVideosCount > 0 &&
      selectedVideosCount < (chapter.videos?.length || 0)
    );
  };

  function EnrollmentMonthOptions({
    months,
    value,
    onChange,
    t,
  }: {
    months: number[];
    value: string;
    onChange: (val: string) => void;
    t: any;
  }) {
    if (!Array.isArray(months) || months?.length === Present.none) {
      return null;
    }
    return (
      <>
        <Stack
          direction="row"
          spacing={4}
          mt={4}
          align="center"
          flexWrap="wrap"
        >
          {months?.map((month) => {
            const isSelected = value === String(month);
            return (
              <EnrollmentMonthBox
                key={month}
                sx={
                  isSelected
                    ? {
                        border: "1px solid #1819B4",
                        color: "#1819B4",
                        backgroundColor: "#F4F4F4",
                        cursor: "pointer",
                        marginBottom: "8px",
                      }
                    : {
                        fontWeight: 400,
                        color: "#2F2F34",
                        cursor: "pointer",
                        marginBottom: "8px",
                      }
                }
                onClick={() => {
                  onChange(String(month));
                }}
              >
                <CalenderIcon color="#026AA2" />
                <Text fontSize={"14px"} fontWeight={500} color={"#026AA2"}>
                  {month}
                  {month > 1
                    ? t("setPreferance.months", " Months")
                    : t("setPreferance.month", " Month")}
                </Text>
              </EnrollmentMonthBox>
            );
          })}
          {enrollmentMonths.length < 12 && (
            <Box
              onClick={() => {
                setEnrollmentMonths((prev) => {
                  const nextValue = Math.max(...prev) + 1;
                  if (nextValue <= 12) {
                    return [...prev, nextValue].sort((a, b) => a - b);
                  }
                  return prev;
                });
              }}
              cursor={"pointer"}
            >
              <BluePlusIcon />
            </Box>
          )}
        </Stack>
      </>
    );
  }

  function EnrollmentOptionsList({
    options,
    control,
    t,
    watch,
  }: {
    options: EnrollmentOption[];
    control: any;
    t: any;
    watch: any;
  }) {
    if (!Array.isArray(options) || options?.length === 0) return null;

    const selectedOption = watch("enrollmentOption");

    return (
      <Controller
        name="enrollmentOption"
        control={control}
        render={({ field }) => (
          <RadioGroup
            value={field?.value}
            onChange={(value) => {
              field.onChange(value);
              // Explicitly set the state based on selected option
              setShowDurationDropdown(value === EnrollmentOptionType.duration);
              setShowPeriodDropdown(value === EnrollmentOptionType.period);
            }}
          >
            <Stack spacing={4}>
              {options.map((option) => (
                <Box key={option?.value}>
                  <OptionRow
                    sx={
                      field?.value === option?.value
                        ? { backgroundColor: "#F4F4F4" }
                        : {}
                    }
                    onClick={() => {
                      field.onChange(option?.value);
                      // Explicitly set the state based on selected option
                      setShowDurationDropdown(
                        option?.value === EnrollmentOptionType.duration
                      );
                      setShowPeriodDropdown(
                        option?.value === EnrollmentOptionType.period
                      );
                    }}
                  >
                    <Radio value={option?.value}>
                      <Text fontSize="16px" fontWeight={500}>
                        {t(option?.labelKey)}
                      </Text>
                    </Radio>
                  </OptionRow>

                  {/* Dropdown-style child under selected radio */}
                  <Collapse
                    in={
                      option?.value === EnrollmentOptionType.duration
                        ? showDurationDropdown
                        : option?.value === EnrollmentOptionType.period
                        ? showPeriodDropdown
                        : false
                    }
                    animateOpacity
                  >
                    {option?.value === EnrollmentOptionType.duration && (
                      <Controller
                        name="enrollmentDurationValue"
                        control={control}
                        render={({ field: durationField }) => (
                          <Box mt={2} pl={6}>
                            <EnrollmentMonthOptions
                              months={enrollmentMonths}
                              value={durationField?.value}
                              onChange={durationField?.onChange}
                              t={t}
                            />
                          </Box>
                        )}
                      />
                    )}
                    {option?.value === EnrollmentOptionType.period && (
                      <Box mt={4} pl={6}>
                        <Schedule />
                        <Box mt={4}>
                          {startDate &&
                            endDate &&
                            RenderSelectedFields({
                              type: "period",
                              data: {
                                start: formatToShortDate(startDate),
                                end: formatToShortDate(endDate),
                              },
                            })}
                        </Box>
                      </Box>
                    )}
                  </Collapse>
                </Box>
              ))}
            </Stack>
          </RadioGroup>
        )}
      />
    );
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <Text fontWeight={600} color={"#2F2F34"} fontSize={"24px"}>
          {t("setPreferance.title")}
        </Text>
        <Text color={"#798692"}>{t("setPreferance.description")}</Text>
      </SectionHeader>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack spacing={6} align="stretch">
            {/* Course Visibility and Access Section */}
            <Card bgColor={"#FFFFFF"}>
              <CardTitle>{t("setPreferance.courseVisibility")}</CardTitle>
              <CardOptionsContainer>
                <CourseAccessOptionsList
                  options={courseAccessOptions}
                  control={control}
                  register={register}
                  watch={watch}
                  chapters={chaptersData || []}
                  selectedChaptersObj={selectedChaptersObj}
                  selectedVideosObj={selectedVideosObj}
                  handleChapterChange={handleChapterChange}
                  handleVideoChange={handleVideoChange}
                  toggleChapterExpansion={toggleChapterExpansion}
                  expandedChapters={expandedChapters}
                  isChapterIndeterminate={isChapterIndeterminate}
                  showChapterDropdown={showChapterDropdown}
                />
              </CardOptionsContainer>
            </Card>

            {/* Enrollment Options Section */}
            <Card bgColor={"#fff"}>
              <CardTitle>{t("setPreferance.enrollmentOptions")}</CardTitle>
              <CardOptionsContainer>
                <EnrollmentOptionsList
                  options={enrollmentOptions}
                  control={control}
                  t={t}
                  watch={watch}
                />
              </CardOptionsContainer>
            </Card>
          </VStack>
        </form>
      </FormProvider>
      <CourseSuccessModal />
    </SectionContainer>
  );
};

export default CourseStep3;
